import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, Wrench, ArrowRight } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import PollWidget from "@/components/PollWidget";

/**
 * Combined section: Newsletter signup + Poll widget side-by-side.
 * Replaces the standalone <Newsletter /> on HomePage so the sonda sits next to subscription.
 */
export default function CommunityEngagement() {
    const [email, setEmail] = useState("");
    const [website, setWebsite] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [hasActivePoll, setHasActivePoll] = useState(false);

    useEffect(() => {
        api.get("/polls/active").then((r) => setHasActivePoll(!!r.data)).catch(() => {});
    }, []);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!email) return;
        setLoading(true);
        try {
            const { data } = await api.post("/newsletter/subscribe", { email, website });
            setSuccess(true);
            setEmail("");
            toast.success(data.message || "Dziękujemy za zapisanie się do newslettera!");
        } catch (err) {
            if (err.response?.status === 429) toast.error("Zbyt wiele prób. Spróbuj ponownie za chwilę.");
            else toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Nie udało się zapisać.");
        } finally { setLoading(false); }
    };

    return (
        <section className="py-16 lg:py-24 bg-[#FDFDFD]" id="newsletter" data-testid="community-engagement">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className={`relative rounded-[2.5rem] bg-gradient-to-br from-[#0D4F3C] to-[#092E22] text-white p-8 md:p-14 overflow-hidden grid gap-10 ${hasActivePoll ? "md:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2"}`}
                >
                    <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#D4A537]/20 blur-3xl pointer-events-none" />

                    {/* Newsletter column */}
                    <div className="relative flex flex-col">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#D4A537]/20 mb-5">
                            <Mail className="text-[#D4A537]" size={24} />
                        </div>
                        <p className="text-[#D4A537] text-[10px] uppercase tracking-[0.35em] font-semibold mb-2">Newsletter</p>
                        <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-3">
                            Bądź na bieżąco z komunikatami
                        </h3>
                        <p className="text-white/75 leading-relaxed mb-5 text-sm md:text-base">
                            Zapisz się, aby otrzymywać powiadomienia o nowych uchwałach,
                            wydarzeniach i ważnych komunikatach wspólnoty.
                        </p>

                        <form onSubmit={onSubmit} className="space-y-3 mt-auto" data-testid="newsletter-form">
                            <input
                                type="text"
                                name="website"
                                value={website}
                                onChange={(e) => setWebsite(e.target.value)}
                                tabIndex={-1}
                                autoComplete="off"
                                style={{ position: "absolute", left: "-10000px", opacity: 0, pointerEvents: "none" }}
                                aria-hidden="true"
                            />
                            <label className="block text-xs uppercase tracking-[0.2em] text-[#D4A537] font-semibold">
                                Twój adres e-mail
                            </label>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <Input
                                    type="email"
                                    required
                                    placeholder="jan.kowalski@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    data-testid="newsletter-email-input"
                                    className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/40 rounded-full h-12 px-5 focus-visible:ring-[#D4A537]"
                                />
                                <Button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="newsletter-submit-button"
                                    className="bg-[#D4A537] hover:bg-[#E5B848] text-[#092E22] rounded-full h-12 px-6 font-semibold transition-all hover:-translate-y-0.5"
                                >
                                    {loading ? "Zapisywanie..." : (<><Send size={16} className="mr-2" /> Zapisz się</>)}
                                </Button>
                            </div>
                            {success && (
                                <p className="flex items-center gap-2 text-emerald-300 text-sm" data-testid="newsletter-success">
                                    <CheckCircle2 size={16} /> Dziękujemy! E-mail został zapisany.
                                </p>
                            )}
                            <p className="text-xs text-white/50">
                                Zapisując się akceptujesz naszą politykę prywatności (RODO).
                            </p>
                        </form>
                    </div>

                    {/* Poll column */}
                    {hasActivePoll && (
                        <div className="relative md:border-l md:border-white/10 md:pl-10">
                            <PollWidget compact />
                        </div>
                    )}

                    {/* Report column */}
                    <div className="relative md:border-l md:border-white/10 md:pl-10 flex flex-col">
                        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#D4A537]/20 mb-5">
                            <Wrench className="text-[#D4A537]" size={24} />
                        </div>
                        <p className="text-[#D4A537] text-[10px] uppercase tracking-[0.35em] font-semibold mb-2">Zgłoś usterkę</p>
                        <h3 className="font-serif text-2xl md:text-3xl leading-tight mb-3">
                            Coś nie działa? Daj nam znać
                        </h3>
                        <p className="text-white/75 leading-relaxed mb-5 text-sm md:text-base">
                            Winda, oświetlenie, hałas, parking, teren wspólny lub garaż — wybierz kategorię,
                            a zgłoszenie trafi <strong className="text-white">bezpośrednio do odpowiedzialnego podmiotu</strong>.
                        </p>
                        <Link
                            to="/zglos-usterke"
                            data-testid="engagement-report-cta"
                            className="group mt-auto inline-flex items-center justify-center gap-2 bg-[#D4A537] hover:bg-[#E5B848] text-[#092E22] rounded-full h-12 px-6 font-semibold transition-all hover:-translate-y-0.5"
                        >
                            Wypełnij formularz
                            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </Link>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
